import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Mail, Lock, User } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleAuthButton, AppleAuthButton } from '@/components/auth/OAuthButtons';
import { TextField, PasswordField, OrDivider, FormError, SubmitButton } from '@/components/auth/FormFields';
import { register, readApiError } from '@/lib/auth-api';

export function SignupPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accept, setAccept] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string; accept?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!displayName.trim()) next.name = t('auth.errors.nameRequired');
    if (!email) next.email = t('auth.errors.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = t('auth.errors.emailInvalid');
    if (!password) next.password = t('auth.errors.passwordRequired');
    else if (password.length < 8) next.password = t('auth.errors.passwordMin');
    if (!accept) next.accept = t('auth.errors.acceptTerms');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    setFormError(null);
    try {
      const { user } = await register({
        displayName: displayName.trim(),
        email: email.trim(),
        password,
        locale: i18n.language?.split('-')[0] || 'en',
      });
      await qc.invalidateQueries({ queryKey: ['currentUser'] });
      // We funnel new accounts into onboarding regardless of verification —
      // the email-verify banner shows inside the app shell.
      nav(user.companyId ? '/app' : '/onboarding', { replace: true });
    } catch (err) {
      setFormError(readApiError(err, t('auth.errors.signupFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.signup.title')}
      subtitle={t('auth.signup.subtitle')}
      footer={
        <p className="text-center">
          {t('auth.signup.haveAccount')}{' '}
          <Link to="/login" className="font-semibold text-primary hover:underline">
            {t('auth.signup.loginLink')}
          </Link>
        </p>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <GoogleAuthButton disabled={loading} />
          <AppleAuthButton disabled={loading} />
        </div>

        <OrDivider label={t('auth.orEmail')} />

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormError message={formError} />

          <TextField
            label={t('auth.fields.name')}
            type="text"
            autoComplete="name"
            placeholder={t('auth.placeholders.name')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={errors.name}
            leadingIcon={<User className="h-4 w-4" />}
            required
          />

          <TextField
            label={t('auth.fields.email')}
            type="email"
            autoComplete="email"
            placeholder={t('auth.placeholders.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            leadingIcon={<Mail className="h-4 w-4" />}
            required
          />

          <PasswordField
            label={t('auth.fields.password')}
            autoComplete="new-password"
            placeholder={t('auth.placeholders.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            leadingIcon={<Lock className="h-4 w-4" />}
            hint={t('auth.passwordHint')}
            showStrength
            required
          />

          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-muted-foreground">
              {t('auth.signup.agree')}{' '}
              <Link to="/terms" className="text-primary hover:underline">{t('auth.signup.terms')}</Link>{' '}
              {t('auth.signup.and')}{' '}
              <Link to="/privacy" className="text-primary hover:underline">{t('auth.signup.privacy')}</Link>.
            </span>
          </label>
          {errors.accept && <p className="text-xs text-destructive">{errors.accept}</p>}

          <SubmitButton loading={loading}>{t('auth.signup.submit')}</SubmitButton>

          <p className="text-center text-[11px] text-muted-foreground">{t('auth.signup.freeNote')}</p>
        </form>
      </div>
    </AuthLayout>
  );
}
